import { ICommit, IRepository, IUser } from '@/types';
import { apiClient, IBaseResponse, getTokenFromCookie, fetchWrapper, getRequestHeaders } from '../apiBase';
import { BASE_API_URL } from '@/config/environment';
import { IRepositoryCommitResponse } from '../repositoryCommitsApi';

export type TDownloadProgress = {
  loaded: number;
  total: number;
  percentCompleted: number;
};

export type TStagedFiles = {
  status: string;
  status_message: string;
  oxen_version: string;
  entries: any[];
  resource?: string | null;
  page_size: number;
  page_number: number;
  total_pages: number;
  total_entries: number;
};

type TAddedDirs = {
  num_files_staged: number;
  total_files: number;
  paths: any;
};

export type TStaged = {
  added_dirs: TAddedDirs;
  added_files: TStagedFiles;
  modified_files: TStagedFiles;
};

export interface IStagedFilesResponse extends IBaseResponse {
  staged: TStaged;
}

interface ICreatedFilesResponse extends IBaseResponse {
  paths: string[];
}

const buildFormData = (files: File[]): FormData => {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('file[]', file);
  });
  return formData;
};

export const createStagedFiles = async ({
  files,
  repository,
  workspaceId,
  resourcePath,
  token,
  onProgress,
}: {
  files: File[];
  repository: IRepository;
  workspaceId: string;
  resourcePath?: string;
  token?: string;
  onProgress: (progress: TDownloadProgress) => void;
}): Promise<ICreatedFilesResponse> => {
  const resourceUrl = `/repos/${repository.namespace}/${repository.name}/workspaces/${workspaceId}/files/${resourcePath}`;
  const requestHeaders = {
    Authorization: `Bearer ${token || getTokenFromCookie()}`,
    'Content-Type': 'multipart/form-data',
  };

  try {
    const res = await apiClient.post(resourceUrl, buildFormData(files), {
      headers: requestHeaders,
      onUploadProgress: (progressEvent) => {
        onProgress({
          loaded: progressEvent?.loaded,
          total: progressEvent?.total || 0,
          percentCompleted: (progressEvent?.progress || 0) * 100,
        });
        // console.log('createStagedFiles > progress', progressEvent);
      },
    });
    return res.data;
  } catch (error: any) {
    return error?.response?.data || { error: { detail: error?.message } };
  }
};

export const commitStagedFiles = async ({
  user,
  repository,
  branch,
  workspaceId,
  commitMessage,
  token,
}: {
  user: IUser;
  repository: IRepository;
  branch: string;
  workspaceId: string;
  commitMessage: string;
  token?: string | undefined;
}): Promise<IRepositoryCommitResponse> => {
  // const userId = user.id || getUserIdFromCookie();
  const resourceUrl = `/repos/${repository.namespace}/${repository.name}/workspaces/${workspaceId}/commit/${branch}`;
  const requestPayload = {
    author: user.username,
    email: user.email,
    message: commitMessage,
  };
  const requestHeaders = getRequestHeaders(token);
  try {
    const res = await apiClient.post(resourceUrl, requestPayload, {
      headers: requestHeaders,
    });
    return res.data;
  } catch (error: any) {
    return error.response.data;
  }
};

export const fetchStagedFiles = async ({
  repository,
  resourcePath,
  token,
  workspaceId,
}: {
  repository: IRepository;
  resourcePath?: string;
  workspaceId: string;
  token?: string;
}): Promise<IStagedFilesResponse> => {
  const targetUrl = `${BASE_API_URL}/repos/${repository.namespace}/${repository.name}/workspaces/${workspaceId}/changes/${resourcePath}`;
  const data = await fetchWrapper({
    resourceName: 'stagedFiles',
    targetUrl,
    authToken: token,
    resourceContext: { namespace: repository.namespace, repositoryName: repository.name, workspaceId },
  });

  return data;
};

export const unstageFile = async ({
  repository,
  workspaceId,
  resourcePath,
  token,
}: {
  repository: IRepository;
  workspaceId: string;
  resourcePath: string;
  token?: string;
}) => {
  const resourceUrl = `/repos/${repository.namespace}/${repository.name}/workspaces/${workspaceId}/changes/${resourcePath}`;
  const requestHeaders = getRequestHeaders(token);
  try {
    const res = await apiClient.delete(resourceUrl, { headers: requestHeaders });
    return res.data;
  } catch (error: any) {
    return error.response.data;
  }
};

const buildStageAndCommitFormData = (files: File[], commitMessage: string): FormData => {
  const formData = new FormData();
  formData.append('commit_message', commitMessage);
  files.forEach((file) => {
    formData.append('file[]', file);
  });
  return formData;
};

type TUploadFilesResponse = IBaseResponse & {
  commit: ICommit;
};

type TUploadFilesParams = {
  files: File[];
  repository: IRepository;
  branch: string;
  workspaceId?: string;
  commitMessage: string;
  resourcePath?: string;
  token?: string;
  onProgress: (progress: TDownloadProgress) => void;
};

export const uploadFiles = async ({
  files,
  repository,
  branch,
  workspaceId,
  commitMessage,
  resourcePath,
  token,
  onProgress,
}: TUploadFilesParams): Promise<TUploadFilesResponse> => {
  const branchPath = [branch, resourcePath].filter(Boolean).join('/');
  const resourceUrl = `/repos/${repository.namespace}/${repository.name}/workspaces/upload/${branchPath}`;
  const requestHeaders = {
    Authorization: `Bearer ${token || getTokenFromCookie()}`,
    'Content-Type': 'multipart/form-data',
  };

  const formData = buildStageAndCommitFormData(files, commitMessage);

  try {
    const res = await apiClient.post(resourceUrl, formData, {
      headers: requestHeaders,
      onUploadProgress: (progressEvent) => {
        onProgress({
          loaded: progressEvent?.loaded,
          total: progressEvent?.total || 0,
          percentCompleted: (progressEvent?.progress || 0) * 100,
        });
      },
    });
    return res.data;
  } catch (error: any) {
    return error?.response?.data || { error: { detail: error?.message } };
  }
};
